import type { FieldSelection } from '#layers/data-model/types/field-selection';
import type { GlobalCollectionId } from '#layers/data-model/types/schema/ids';

export interface ExpandFieldSelectionOptions {
	collectionId: GlobalCollectionId;
	selection: FieldSelection;
	resolveField: (collectionId: GlobalCollectionId, apiName: string) => {
		isRelation: boolean;
		oppositeCollectionId?: GlobalCollectionId;
	} | undefined;
	getCollectionKeyFieldNames: (collectionId: GlobalCollectionId) => string[];
	getRelationJoinFields: (collectionId: GlobalCollectionId, relationApiName: string) => {
		ownFields: string[];
		oppositeFields: string[];
	};
}

export function expandRequiredFields(options: ExpandFieldSelectionOptions): FieldSelection {
	return expandAtLevel(options.collectionId, options.selection, options);
}

function expandAtLevel(
	collectionId: GlobalCollectionId,
	selection: FieldSelection,
	options: ExpandFieldSelectionOptions,
): FieldSelection {
	const result: FieldSelection = { ...selection };
	const ownFields = new Set(options.getCollectionKeyFieldNames(collectionId));

	if (selection.include) {
		result.include = {};

		for (const [name, query] of Object.entries(selection.include)) {
			const field = options.resolveField(collectionId, name);

			if (!field?.isRelation || !field.oppositeCollectionId) {
				result.include[name] = query;
				continue;
			}

			const joinFields = options.getRelationJoinFields(collectionId, name);
			const nested = expandAtLevel(field.oppositeCollectionId, query, options);
			result.include[name] = injectPrimitives(nested, joinFields.oppositeFields);
			for (const name of joinFields.ownFields) ownFields.add(name);
		}
	}

	return injectPrimitives(result, [...ownFields]);
}

function injectPrimitives(selection: FieldSelection, fields: string[]): FieldSelection {
	const primitives = selection.fields ?? ['*'];
	if (fields.length === 0 || primitives.includes('*')) return selection;
	return { ...selection, fields: [...new Set([...primitives, ...fields])] };
}
